import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import VisibilityActionButton from '../../../components/designe/ActionButtons/VisibilityActionButton';
import AgreementsLinks from '../../../components/reusable/agreements/AgreementsLinks';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useFeature } from '../../../hooks/use-feature';
import { AppFeatureKey } from '../../../shared/constants';
import { Paths } from '../../../shared/paths';
import {
  AuthPrimaryButton,
  AuthSecondaryButton,
} from '../components/auth-button';
import AuthLanguageSelector from '../components/auth-language-selector/AuthLanguageSelector';
import AuthTextField from '../components/auth-text-field';
import { useStyles } from './SignUp.styles';
import { useSignUpPage } from './useSignUpPage';

const SignUp = () => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { navigate } = useNavigationStack();

  const { classes, cx } = useStyles(theme);
  const {
    [AppFeatureKey.appleAuthorization]: appleAuthorizationActive,
    [AppFeatureKey.facebookAuthorization]: facebookAuthorizationActive,
  } = useFeature();

  const {
    formik,
    handleSubmitAttempt,
    handleSocialSignUpAttempt,
    signUpWithGoogle,
    signUpWithApple,
    signUpWithFacebook,
  } = useSignUpPage(t, {
    afterEmailSignUp: () =>
      navigate(Paths.signUpVerification, { replace: true }),
    afterSocialSignUp: () => navigate(Paths.root, { replace: true }),
  });

  const agreementsError =
    !!formik.touched.acceptedAgreements &&
    Boolean(formik.errors.acceptedAgreements);

  return (
    <div className={classes.signUpWrapper}>
      {isMobile ? null : (
        <AuthLanguageSelector className={classes.desktopLanguageSelector} />
      )}
      <div className={classes.signUpContentWrapper}>
        {isMobile && (
          <AuthLanguageSelector className={classes.mobileLanguageSelector} />
        )}
        <form onSubmit={handleSubmitAttempt} className={classes.signUpForm}>
          <AuthTextField
            placeholder={t('common.email')}
            fullWidth
            name="email"
            formik={formik}
            className={classes.textField}
          />
          <AuthTextField
            placeholder={t('signUp.password')}
            fullWidth
            name="password"
            type={showPassword ? 'text' : 'password'}
            className={classes.textField}
            errorIconRightPadding={40}
            formik={formik}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <VisibilityActionButton
                    isActive={showPassword}
                    onClick={() => setShowPassword(!showPassword)}
                  />
                </InputAdornment>
              ),
              disableUnderline: true,
            }}
          />
          <AuthTextField
            placeholder={t('signUp.confirmPassword')}
            fullWidth
            name="confirmPassword"
            type={showPassword ? 'text' : 'password'}
            className={classes.textField}
            errorIconRightPadding={40}
            formik={formik}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <VisibilityActionButton
                    isActive={showPassword}
                    onClick={() => setShowPassword(!showPassword)}
                  />
                </InputAdornment>
              ),
              disableUnderline: true,
            }}
          />

          <FormControl
            component="fieldset"
            error={agreementsError}
            variant="standard"
            className={cx(
              classes.agreementsCheckbox,
              agreementsError && classes.agreementsCheckboxError,
            )}
            fullWidth>
            <FormControlLabel
              control={
                <Checkbox
                  name="acceptedAgreements"
                  checked={!!formik.values.acceptedAgreements}
                  onChange={formik.handleChange}
                />
              }
              label={<AgreementsLinks />}
            />
            {formik.touched.acceptedAgreements &&
              formik.errors.acceptedAgreements && (
                <FormHelperText error>
                  {formik.errors.acceptedAgreements}
                </FormHelperText>
              )}
          </FormControl>
          <AuthPrimaryButton
            type="submit"
            fullWidth
            className={classes.signUpSubmitButton}>
            {t('signUp.signUp')}
          </AuthPrimaryButton>
        </form>

        <Divider className={classes.signUpDivider}>
          <Typography variant="body1">{t('signIn.or')}</Typography>
        </Divider>

        <Box display="flex" gap={2} flexDirection="column" width="100%">
          <AuthSecondaryButton
            className={classes.signUpButtonWithIcon}
            fullWidth
            onClick={() => handleSocialSignUpAttempt(signUpWithGoogle)}>
            <GoogleIcon />
            {t('signUp.signUpWithGoogle')}
          </AuthSecondaryButton>
          {facebookAuthorizationActive && (
            <AuthSecondaryButton
              className={classes.signUpButtonWithIcon}
              fullWidth
              onClick={() => handleSocialSignUpAttempt(signUpWithFacebook)}>
              <FacebookIcon />
              {t('signUp.signUpWithFacebook')}
            </AuthSecondaryButton>
          )}
          {appleAuthorizationActive && (
            <AuthSecondaryButton
              className={classes.signUpButtonWithIcon}
              fullWidth
              onClick={() => handleSocialSignUpAttempt(signUpWithApple)}>
              <AppleIcon />
              {t('signUp.signUpWithApple')}
            </AuthSecondaryButton>
          )}
        </Box>

        <div className={classes.signUpModeSwitcher}>
          {t('signUp.existingUser')}
          <Link className={classes.signUpModeSwitcherButton} to={Paths.signIn}>
            {t('signUp.signIn')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SignUp;

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24">
    <path
      fill="currentColor"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="currentColor"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="currentColor"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="currentColor"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M13.5 8H16V5h-2.5C10.46 5 8 7.46 8 10.5V13H6v3h2v5h3v-5h3l.5-3H11v-2.5C11 9.67 11.67 9 12.5 9H13.5V8z" />
  </svg>
);
